/**
 * components/BiorhythmChart.js — Биоритмын 3 долгионы график.
 *
 * Backend нь ЗӨВХӨН тухайн өдрийн утгыг буцаадаг ч `days` (төрснөөс хойшх
 * хоног) талбарыг өгдөг. Долгион нь sin(2π·d/T) тул хүссэн өдрийн утгыг
 * фронтэнд өөрөө бодож чадна — нэмэлт API дуудлага, нэмэлт дата хэрэггүй.
 *
 * Номын сан ашиглаагүй: 3 муруйд chart.js татах нь bundle-д илүүдэл, мөн
 * popover дотор шууд гарч ирэх шаардлагатай.
 */
import React from "react";

const CYCLES = [
  { key: "physical", period: 23, label: "Бие махбод", color: "#DC2626" },
  { key: "emotional", period: 28, label: "Сэтгэл хөдлөл", color: "#2563EB" },
  { key: "intellectual", period: 33, label: "Оюун ухаан", color: "#16A34A" },
];

const wave = (days, period) => Math.sin((2 * Math.PI * days) / period);

export default function BiorhythmChart({
  bio,
  range = 15, // өнөөдрөөс хойш/урагш хэдэн хоног
  width = 300,
  height = 120,
}) {
  if (!bio || typeof bio.days !== "number") return null;

  const pad = { l: 6, r: 6, t: 8, b: 14 };
  const w = width - pad.l - pad.r;
  const h = height - pad.t - pad.b;
  const mid = pad.t + h / 2;

  const x = (off) => pad.l + ((off + range) / (range * 2)) * w;
  const y = (v) => mid - v * (h / 2);

  // Муруй тус бүрийг 0.5 хоногийн алхмаар — жигд харагдана
  const path = (period) => {
    const pts = [];
    for (let off = -range; off <= range; off += 0.5) {
      pts.push(`${x(off).toFixed(1)},${y(wave(bio.days + off, period)).toFixed(1)}`);
    }
    return "M" + pts.join(" L");
  };

  return (
    <div style={{ width }}>
      <svg width={width} height={height} style={{ display: "block" }}>
        {/* Тэгийн шугам — үүнийг огтлох нь «эгзэгтэй өдөр» */}
        <line
          x1={pad.l} y1={mid} x2={pad.l + w} y2={mid}
          stroke="rgba(255,255,255,0.35)" strokeWidth="1"
        />
        {/* ±100% хязгаар */}
        {[-1, 1].map((v) => (
          <line
            key={v}
            x1={pad.l} y1={y(v)} x2={pad.l + w} y2={y(v)}
            stroke="rgba(255,255,255,0.12)" strokeDasharray="2 3"
          />
        ))}
        {/* Өнөөдөр */}
        <line
          x1={x(0)} y1={pad.t} x2={x(0)} y2={pad.t + h}
          stroke="rgba(255,255,255,0.55)" strokeWidth="1.5"
        />
        <text
          x={x(0)} y={height - 3} fontSize="9"
          fill="rgba(255,255,255,0.75)" textAnchor="middle"
        >
          өнөөдөр
        </text>

        {CYCLES.map((c) => (
          <g key={c.key}>
            <path d={path(c.period)} fill="none" stroke={c.color} strokeWidth="1.8" />
            <circle
              cx={x(0)} cy={y(wave(bio.days, c.period))} r="3"
              fill={c.color} stroke="#fff" strokeWidth="1"
            />
          </g>
        ))}
      </svg>

      <div style={{ display: "flex", flexDirection: "column", gap: 1, marginTop: 4 }}>
        {CYCLES.map((c) => {
          const cur = bio.cycles?.[c.key];
          const flagged =
            (bio.critical || []).includes(c.key) || (bio.low || []).includes(c.key);
          return (
            <div key={c.key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}>
              <span style={{ width: 10, height: 2.5, background: c.color, borderRadius: 2 }} />
              <span style={{ flex: 1, opacity: 0.85 }}>{c.label}</span>
              <b style={{ color: flagged ? "#FDE68A" : "#fff" }}>
                {cur ? `${cur.percent > 0 ? "+" : ""}${cur.percent}%` : "—"}
              </b>
            </div>
          );
        })}
      </div>
    </div>
  );
}
